// Blocs du lot 3 insérés dans les accueils des autres profils (charte D-038, exceptions d'abord) : livraisons en
// cours du réseau du distributeur (suivi des missions, E3) et chargements du jour du dépôt (E2). Chaque bloc lit
// la même route que sa page et renvoie vers elle ; une valeur absente est annoncée avec son motif.

import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { deProduit } from '@carburanttrack/shared/logistique';
import { api } from '../../api/client';
import { MessageErreur, Squelette, Vide } from '../../composants/Etats';
import { dateHeure } from '../../composants/format';
import { Icone } from '../../composants/Icones';
import { EtiquetteException, exceptionMission, quantite, resumeOrdre } from './communs';
import type { LigneOrdre, SuiviMissions } from './types';

const LIGNES_AFFICHEES = 5;

/** Livraisons en cours du périmètre (accueil « Mon réseau » du distributeur) : exceptions d'abord, puis la page. */
export function BlocLivraisonsEnCours() {
  const suivi = useQuery({ queryKey: ['suivi-missions'], queryFn: async () => (await api<SuiviMissions>('/api/v1/suivi/missions')).donnees, refetchInterval: 120_000 });
  const missions = suivi.data ? [...suivi.data.missions.filter((m) => exceptionMission(m)), ...suivi.data.missions.filter((m) => !exceptionMission(m))] : [];
  return (
    <section className="carte-bloc" aria-labelledby="titre-livraisons">
      <div className="carte-bloc-tete">
        <h2 id="titre-livraisons">Livraisons en cours</h2>
        {suivi.data && (
          <span className="attenue">
            {suivi.data.compteurs.total} mission{suivi.data.compteurs.total > 1 ? 's' : ''}
            {suivi.data.compteurs.enRetard ? `, ${suivi.data.compteurs.enRetard} en retard` : ''}
            {suivi.data.compteurs.positionsAnciennes ? `, ${suivi.data.compteurs.positionsAnciennes} sans position récente` : ''}
          </span>
        )}
      </div>
      {suivi.isLoading && <Squelette lignes={3} libelle="Chargement des livraisons en cours" />}
      {suivi.error && <MessageErreur erreur={suivi.error} titre="Livraisons indisponibles" />}
      {suivi.data && missions.length === 0 && <Vide>Aucune livraison en cours dans votre périmètre.</Vide>}
      {missions.length > 0 && (
        <ul className="liste-lignes">
          {missions.slice(0, LIGNES_AFFICHEES).map((m) => {
            const e = exceptionMission(m);
            return (
              <li key={m.id}>
                <Link to={`/livraisons/${m.id}`}>
                  <span>
                    <b>
                      {m.numero}, {resumeOrdre(m)} <EtiquetteException exception={e} />
                    </b>
                    <small>
                      {m.etatLibelle}
                      {m.position ? ` ; ${m.position.libelle}` : ''}
                      {m.dateArriveePrevue ? ` ; arrivée prévue ${dateHeure(m.dateArriveePrevue)}` : ''}
                    </small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      {suivi.data && (
        <p className="lien-suite">
          <Link to="/livraisons">Toutes les livraisons</Link>
        </p>
      )}
    </section>
  );
}

interface VueChargements {
  aConfirmer: LigneOrdre[];
  confirmesDuJour: LigneOrdre[];
}

/** Chargements du jour du dépôt (accueil de l'opérateur) : ordres à confirmer d'abord, puis la page. */
export function BlocChargementsDuJour() {
  const q = useQuery({ queryKey: ['chargements'], queryFn: async () => (await api<VueChargements>('/api/v1/chargements')).donnees, refetchInterval: 120_000 });
  const maintenant = Date.now();
  return (
    <section className="carte-bloc" aria-labelledby="titre-chargements">
      <div className="carte-bloc-tete">
        <h2 id="titre-chargements">Chargements du jour</h2>
        {q.data && (
          <span className="attenue">
            {q.data.aConfirmer.length} à confirmer, {q.data.confirmesDuJour.length} confirmé{q.data.confirmesDuJour.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
      {q.isLoading && <Squelette lignes={3} libelle="Chargement des ordres à confirmer" />}
      {q.error && <MessageErreur erreur={q.error} titre="Chargements indisponibles" />}
      {q.data && q.data.aConfirmer.length === 0 && <Vide>Aucun chargement à confirmer.</Vide>}
      {q.data && q.data.aConfirmer.length > 0 && (
        <ul className="liste-lignes">
          {q.data.aConfirmer.slice(0, LIGNES_AFFICHEES).map((o) => (
            <li key={o.id}>
              <Link to={`/chargements/${o.id}`}>
                <span>
                  <b>
                    {o.numero}, {quantite(o.volumePrevu, o.unite)} {deProduit(o.produitCode)} {o.prioritaire && <span className="etiquette rouge">Prioritaire</span>}{' '}
                    {Date.parse(o.dateChargementPrevue) < maintenant && <span className="etiquette ambre">Heure prévue dépassée</span>}
                  </b>
                  <small>
                    {o.proprietaire.nom}, vers {o.destination.nom} ; prévu {dateHeure(o.dateChargementPrevue)} ; {o.transporteur.nom}
                  </small>
                </span>
                <Icone nom="chevron" />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {q.data && (
        <p className="lien-suite">
          <Link to="/chargements">Tous les chargements</Link>
        </p>
      )}
    </section>
  );
}
